import React, { useEffect, useRef, useState } from 'react'
import { 
  Box,
  Divider,
  FormLabel,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Text,
  useOutsideClick
} from '@chakra-ui/react'
import banks from '../../__mocks__/banks.json'
import { ChevronDownIcon, CloseIcon, Search2Icon } from '@chakra-ui/icons'

interface Bank {
  name: string
  icon: string
}

interface SelectWithSearchProps {
  onBankSelect: (selectedBank: Bank | null) => void
  onBlur: () => void
  isRequired: boolean
}

const SearchSelect: React.FC<SelectWithSearchProps> = ({ onBankSelect, onBlur }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null)
  const [filteredOptions, setFilteredOptions] = useState<Bank[]>(banks)
  const [showRequiredMessage, setShowRequiredMessage] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const changeColor = showRequiredMessage && !selectedBank ? "danger.100" : "neutral.90"

  const orderBank = (a, b) => {
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    }
    return 0;
  }

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (isOpen) {
        setIsOpen(false)
        onBlur()
        if (!selectedBank) {
          setShowRequiredMessage(true)
        }
      }
    },
  })

  useEffect(() => {
    setFilteredOptions(
      banks.filter(option =>
        option.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    )
  }, [searchValue])


  useEffect(() => {
    banks.sort(orderBank)
    setFilteredOptions(banks)
  }, [])
  


  const handleToggle = () => {
    setIsOpen(prev => !prev)
    if (!isOpen) {
      inputRef.current?.focus()
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const handleSelectChange = (bank: Bank) => {
    setSelectedBank(bank)
    onBankSelect(bank)
    setIsOpen(false)
    setShowRequiredMessage(false)
  }

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedBank(null)
    setSearchValue('')
    onBankSelect(null)    
    setShowRequiredMessage(true)
  }
  
  return (
    <Box ref={containerRef} width="100%">
      <FormLabel textStyle='labelMd' display='contents' color={showRequiredMessage && !selectedBank ? "danger.100" : "grey.120"}>
        Banco
      </FormLabel>
      <Box
        border="1px solid"
        borderColor={changeColor}
        borderRadius="md"
        p="2"
        cursor="pointer"
        onClick={handleToggle}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        color='neutral.90'
        data-testid='select-bank'
      >
        <Text textStyle='bodyMd' color={changeColor}>{selectedBank ? selectedBank.name : 'Seleccioná un banco'}</Text>
        <Box display="flex" alignItems="center">
          {selectedBank && (
            <IconButton
              aria-label="ClearSelection"
              icon={<CloseIcon color='secondary.100'/>}
              size="sm"
              onClick={clearSelection}
              data-testid='clearSelection'
              variant="ghost"
              mr={2}
            />
          )}
          <ChevronDownIcon boxSize='1.5em' color='secondary.100'/>
        </Box>
      </Box>
      {isOpen && (
        <Box
          border="1px solid"
          borderColor="grey.100"
          borderRadius="md"
          bg="white"
          boxShadow="md"
          zIndex="11"
          width="100%"
          maxH="235px"
          position='absolute'
          overflowY="auto"
          display="flex"
          flexDirection="column"
          alignItems="center"
          data-testid='bankList-container'
        >
          <Box width="348px" mt="2">
            <InputGroup>
              <InputLeftElement pointerEvents='none'>
                <Search2Icon color='grey.100' boxSize='1.2em'/>
              </InputLeftElement>
              <Input
                ref={inputRef}
                placeholder="Podés buscar por nombre"
                value={searchValue}
                onChange={handleSearchChange}
                border="2px solid"
                borderColor="gray.300"
                data-testid='bank-searchInput'
                p="2"
                borderRadius="md"
                pl="10"
                _focusVisible={{ borderColor: 'grey.120' }}
              />
              {searchValue && (
                <InputRightElement>
                  <IconButton
                    aria-label="ClearSearch"
                    icon={<CloseIcon color='secondary.100'/>}
                    size="sm"
                    onClick={clearSelection}
                    data-testid='clearSelection-search'
                    variant="ghost"
                  />
                </InputRightElement>
              )}
            </InputGroup>  
          </Box>
          <List spacing={1} mt="2" width="348px">
            <Divider 
              orientation='horizontal' 
              h='6px' 
              borderBottomWidth='2px'
              w='344px'
            />
            {filteredOptions.map((option, index) => (
            <ListItem
              key={index}
              p="2"
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              display="flex"
              alignItems="center"
              data-testid='bankList-Item'
              onClick={() => handleSelectChange(option)}
            >
              <Image 
                src={require(`../../assets/bancos/${option.icon}`)}
                boxSize="24px"
                mr='10px'
              />
              {option.name}
            </ListItem>
            ))}
          </List>
        </Box>
      )}
      {showRequiredMessage && !selectedBank ? (
        <Text textStyle='labelSm' mt={0} color='danger.100'>
          Este campo es requerido
        </Text>
      ) : null}
    </Box>
  )
}

export default SearchSelect